import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { SiteLocationsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as SiteLocationActions from "../../../actions/siteLocations";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./SiteLocationsPage.scss";

interface SiteLocationsPageProps {
    formFilter: IFormFilter;
    siteLocation: Dtos.SiteLocation[];
    loadingSiteLocations: boolean;
    loadSiteLocationsSuccess: boolean;
    loadSiteLocationsFailure: boolean;
    loadSiteLocationsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearSiteLocations: SiteLocationActions.ISiteLocationClearActionCreator;
    loadSiteLocations: SiteLocationActions.ISiteLocationLoadActionCreator;
    filter: SiteLocationActions.ISiteLocationFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const SiteLocationsPage: FC<SiteLocationsPageProps> = ({
    formFilter,
    siteLocation,
    loadingSiteLocations,
    loadSiteLocationsSuccess,
    loadSiteLocationsFailure,
    loadSiteLocationsErrorCode,
    permissions,
    pageSize,
    clearSiteLocations,
    loadSiteLocations,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showSiteLocations = !loadingSiteLocations && loadSiteLocationsSuccess;


    // Load site locations
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(SiteLocationsBreadcrumbs());

            loadSiteLocations();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        SiteLocationsBreadcrumbs,
        loadSiteLocations,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadSiteLocationsFailure) {
            navigate(`/error/${loadSiteLocationsErrorCode}`);
        }
    }, [loadSiteLocationsFailure, loadSiteLocationsErrorCode, navigate]);

    // Cleanup effect to clear the site locations on component unmount
    useEffect(() => {
        return () => {
            clearSiteLocations();
        };
    }, [clearSiteLocations]);

    const createTable = useMemo(() => {
        if (!siteLocation || siteLocation.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no site locations to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = siteLocation.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={siteLocation}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : siteLocation.length
                    }
                    pageSize={showPagination ? pageSize : siteLocation.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no site locations to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/institution/sitelocations/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [siteLocation, pageSize, PAGE_SIZE_OPTIONS, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Site Locations</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.SiteLocationsCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/institution/sitelocations/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showSiteLocations ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showSiteLocations ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showSiteLocations, createTable]);

    return (
        <InstitutionLookupsLayout
            loading={loadingSiteLocations}
            permission={Dtos.Permission.SiteLocationsView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<SiteLocationsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.siteLocations.formFilter,
        siteLocation: state.siteLocations.formFilteredData,
        loadingSiteLocations:
            state.siteLocations.loadState &&
            state.siteLocations.loadState.status == RequestState.Pending,
        loadSiteLocationsSuccess:
            state.siteLocations.loadState &&
            state.siteLocations.loadState.status == RequestState.Success,
        loadSiteLocationsFailure:
            state.siteLocations.loadState &&
            state.siteLocations.loadState.status == RequestState.Failure,
        loadSiteLocationsErrorCode: state.siteLocations.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<SiteLocationsPageProps> => {
    return {
        loadSiteLocations: bindActionCreators(
            SiteLocationActions.LoadSiteLocation,
            dispatch
        ),
        clearSiteLocations: bindActionCreators(
            SiteLocationActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(SiteLocationActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SiteLocationsPage);
